// src/hooks/useBoundaryVisibility.js
import { useCallback } from 'react'
import { useCityBoundaries } from './useCityBoundaries'

/**
 * Hook para controlar la visibilidad de las capas de límites
 * @param {Object} map - Instancia del mapa de Mapbox
 * @param {boolean} mapLoaded - Estado de carga del mapa
 * @returns {Object} - Funciones para controlar visibilidad
 */
export const useBoundaryVisibility = (map, mapLoaded) => {
  const { currentCity } = useCityBoundaries()
  const hasStyle = useCallback(() => {
    if (!map) return false
    try {
      return Boolean(map.getStyle?.())
    } catch {
      return false
    }
  }, [map])

  /**
   * Cambia la visibilidad de las capas de límites
   * @param {boolean} visible - true para mostrar, false para ocultar
   */
  const setBoundaryVisibility = useCallback(
    visible => {
      if (!map || !mapLoaded || !currentCity || !hasStyle()) return

      const lineLayerId = `boundary-line-${currentCity}`
      const fillLayerId = `boundary-fill-${currentCity}`

      try {
        const visibility = visible ? 'visible' : 'none'

        // Cambiar visibilidad de ambas capas
        if (map.getLayer(lineLayerId)) {
          map.setLayoutProperty(lineLayerId, 'visibility', visibility)
        }

        if (map.getLayer(fillLayerId)) {
          map.setLayoutProperty(fillLayerId, 'visibility', visibility)
        }
      } catch (error) {
        console.error('Error al cambiar visibilidad de límites:', error)
      }
    },
    [map, mapLoaded, currentCity, hasStyle]
  )

  /**
   * Obtiene el estado actual de visibilidad
   * @returns {boolean} - true si visible, false si oculto
   */
  const getBoundaryVisibility = useCallback(() => {
    if (!map || !mapLoaded || !currentCity || !hasStyle()) return false

    const lineLayerId = `boundary-line-${currentCity}`

    try {
      if (map.getLayer(lineLayerId)) {
        const visibility = map.getLayoutProperty(lineLayerId, 'visibility')
        return visibility !== 'none'
      }
    } catch (error) {
      console.error('Error al obtener visibilidad:', error)
    }

    return true
  }, [map, mapLoaded, currentCity, hasStyle])

  /**
   * Toggle de visibilidad
   */
  const toggleBoundaryVisibility = useCallback(() => {
    const currentVisibility = getBoundaryVisibility()
    setBoundaryVisibility(!currentVisibility)
  }, [getBoundaryVisibility, setBoundaryVisibility])

  return {
    setBoundaryVisibility,
    getBoundaryVisibility,
    toggleBoundaryVisibility,
  }
}
